import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import {
  Spin, Rate, Row, Col,
} from 'antd';
import Helmet from 'react-helmet';
import { Link } from 'react-router-dom';
import {
  isEmpty, compose, groupBy, map, defaultTo, flip, prop, keys, values, mapObjIndexed,
} from 'ramda';
import classNames from 'classnames';
import StartIcon from 'material-design-icons/action/svg/production/ic_star_rate_18px.svg';
import ReportIcon from 'material-design-icons/action/svg/production/ic_report_problem_24px.svg';

import { DownloadReleaseButton, DownloadTrackButton } from '../DownloadButton';
import Pagination, { getReleaseList } from '../Pagination';
import ReleaseCard from '../ReleaseCard';
import TrackItem from '../TrackItem';
import { BuyTrackButton } from '../BuyButton/BuyButton';
import SheetTitle from '../SheetTitle';
import { slugify } from '../../helpers/view';
import AddThis from '../AddThis/AddThis';

import c from './Release.less';

const roundToHalf = (num) => Math.round(num * 2) / 2;

/**
 * @return {null}
 */
export function ReleaseGenres({ genres, className, tagClassName }) {
  return genres ? (
    <ul className={classNames(c.genresList, className)}>
      {
        genres.slice(0, 4).map((genre) => (
          <li key={genre.id} className={classNames(c.tag, tagClassName)}>
            <Link to={`/genre/${slugify(genre.name, genre.id)}`}>{genre.name}</Link>
          </li>
        ))
      }
    </ul>
  ) : null;
}

ReleaseGenres.propTypes = {
  genres: PropTypes.array.isRequired,
  className: PropTypes.string,
  tagClassName: PropTypes.string,
};

ReleaseGenres.defaultProps = {
  className: null,
  tagClassName: null,
};

/**
 * @return {null}
 */
export function ReleaseSeries({ release, seriesList }) {
  const series = seriesList[release.series] || {};

  return release.series ? (
    <Link to={`/series/${slugify(series.name, series.id)}`}>
      #{series.name}
    </Link>
  ) : null;
}

ReleaseSeries.propTypes = {
  release: PropTypes.object.isRequired,
  seriesList: PropTypes.object.isRequired,
};

/**
 * @return {null}
 */

export function ReleaseRestAlbums(props) {
  const {
    pagination, artistMoreReleaseList, releaseList, releaseId, artistList, artist, onLoadMore,
  } = props;
  const releaseIdList = artistMoreReleaseList.ids
    .filter((id) => id !== Number(releaseId))
    .slice(0, pagination.portion * pagination.limit);

  const restReleases = getReleaseList(
    releaseIdList,
    releaseList,
    artistList
  );

  return !isEmpty(restReleases) ? (
    <div className={c.moreByArtistBlock}>
      <SheetTitle title={`Also by ${artist.name}`} />
      <Row
        gutter={{
          xs: 10, sm: 16, md: 20, lg: 20,
        }}
        className={c.artistReleases}
      >
        {
          restReleases.map((artistRelease) => (
            <Col xs={12} sm={6} md={6} lg={4} key={artistRelease.id}>
              <ReleaseCard showArtist={false} release={artistRelease} type="light" />
            </Col>
          ))
        }
      </Row>

      <Pagination
        key="pagination"
        total={artistMoreReleaseList.total}
        pageSize={pagination.limit}
        currentPage={pagination.portion}
        isLoading={releaseList.loading}
        onLoadMore={onLoadMore}
        showPages={false}
        showMoreTitle="Show More"
      />
    </div>
  ) : null;
}

ReleaseRestAlbums.propTypes = {
  artistMoreReleaseList: PropTypes.object.isRequired,
  releaseList: PropTypes.object.isRequired,
  artistList: PropTypes.object.isRequired,
  releaseId: PropTypes.number.isRequired,
  artist: PropTypes.object.isRequired,
  pagination: PropTypes.object.isRequired,
  onLoadMore: PropTypes.func.isRequired,
};

export function ReleaseTracksSection(props) {
  const {
    release, artistList, trackList, tracksLoading, showDownloadLink, onEndTrack, showHeader,
  } = props;
  const groupedByDisks = compose(
    groupBy(prop('discNumber')),
    defaultTo({}),
    map(flip(prop)(trackList)),
    defaultTo([])
  )(release.tracks);

  const handleEndTrack = () => {
    onEndTrack();
  };

  const hasMultipleDisks = keys(groupedByDisks).length > 1;
  const releaseFileSize = (release.tracks || [])
    .map((trackId) => trackList[trackId])
    .reduce((acc, track) => acc + track.fileSize, 0);
  const releaseWasDownloaded = (release.tracks || [])
    .map((trackId) => {
      return trackList[trackId];
    })
    .every((track) => !!track.lastDownloadedTime);

  const releaseTitle = () => {
    if (release) {
      const artist = artistList.data[release.artist];

      if (artist) {
        return `${release.name} by ${artist.name}`;
      }
    }
    return null;
  };

  return (
    <Fragment>
      {showHeader && (
        <SheetTitle
          title="Tracks"
          id="tracks"
          rightBlock={
            showDownloadLink && release.downloadUrl && (
              <DownloadReleaseButton
                className={c.downloadButton}
                url={release.downloadUrl}
                fileSize={releaseFileSize}
                wasDownloaded={releaseWasDownloaded}
                ariaLabel={`Download release ${releaseTitle}`}
              />
            )
          }
        />
      )}
      <Spin spinning={tracksLoading} size="large" delay={1000}>
        <div className={c.tracksContainer}>
          {
            release.tracks && compose(
              values,
              mapObjIndexed(
                (disk, diskNo) => (
                  <div key={diskNo}>
                    {
                      hasMultipleDisks && (
                        <h3 className={c.diskNo}>
                          Disk #{diskNo}
                        </h3>
                      )
                    }
                    {
                      disk.map((track) => {
                        const artist = artistList.data[track.artist];

                        return (
                          <TrackItem
                            key={track.id}
                            artist={['Soundtrack', 'VA_Compilation'].includes(release.type) ? artist : {}}
                            track={track}
                            showPosition
                            onEndTrack={handleEndTrack}
                            actions={
                              track.downloadUrl ? (
                                <DownloadTrackButton
                                  className={c.downloadTrackButton}
                                  url={track.downloadUrl}
                                  fileSize={track.fileSize}
                                  wasDownloaded={!!track.lastDownloadedTime}
                                  ariaLabel={`Download track ${track.name}`}
                                />
                              ) : (
                                <BuyTrackButton
                                  track={track}
                                  alt={`Buy ${track.name} mp3 song by ${artist.name}`}
                                />
                              )
                            }
                          />
                        );
                      })
                    }
                  </div>
                )
              )
            )(groupedByDisks)
          }
        </div>
      </Spin>
    </Fragment>
  );
}

ReleaseTracksSection.propTypes = {
  release: PropTypes.object.isRequired,
  artistList: PropTypes.object.isRequired,
  trackList: PropTypes.object.isRequired,
  tracksLoading: PropTypes.bool.isRequired,
  showDownloadLink: PropTypes.bool,
  showHeader: PropTypes.bool,
  onEndTrack: PropTypes.func,
};

ReleaseTracksSection.defaultProps = {
  showDownloadLink: true,
  showHeader: true,
  onEndTrack: () => null,
};

export function ReleaseArtist(props) {
  const {
    release, artist, trackList, className,
  } = props;
  const isVARelease = (release.tracks || [])
    .map((id) => trackList[id])
    .every((track) => track.artistId !== release.artist);

  const artistTextOrLink = isVARelease ? (
    <span>{artist.name}</span>
  ) : (
    <Link to={`/artist/${slugify(artist.name, artist.id)}`}>{artist.name}</Link>
  );

  return (
    <span className={classNames(c.artist, className)}>by {artistTextOrLink}</span>
  );
}

ReleaseArtist.propTypes = {
  release: PropTypes.object.isRequired,
  artist: PropTypes.object.isRequired,
  trackList: PropTypes.object.isRequired,
  className: PropTypes.string,
};

ReleaseArtist.defaultProps = {
  className: null,
};

export function ReleaseHelmet(props) {
  const {
    description, title, url, release, artist, children,
  } = props;
  return (
    <Helmet defer={false}>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta property="og:type" content="music.album" />
      <meta property="og:title" content={release.name} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={`${release.coverUrl}/230.jpg`} />
      <meta property="og:description" content={description} />
      <meta property="music:album:track" content={release.tracksCount} />
      <meta
        property="music:musician"
        content={`https://mp3caprice.com/artists/${slugify(artist.name, artist.id)}`}
      />
      {children}
    </Helmet>
  );
}

ReleaseHelmet.propTypes = {
  release: PropTypes.object.isRequired,
  artist: PropTypes.object.isRequired,
  description: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired,
  children: PropTypes.arrayOf(PropTypes.element),
};

ReleaseHelmet.defaultProps = {
  children: [<Fragment />],
};

export function ReleaseRateAndShare(props) {
  const {
    share,
    release,
    reviews,
    rateClassName,
    shareClassName,
    showComplain,
    onComplainClick,
    reportClassName,
    buttonsWrapClassName,
    containerClassName,
  } = props;

  return (
    <div className={classNames(c.rateAndShare, containerClassName)}>
      <ReleaseRate release={release} reviews={reviews} className={rateClassName} />
      <div className={buttonsWrapClassName}>
        <AddThis className={shareClassName} url={share.url} title={share.title} description={share.description} />
        {
          showComplain && (
            <button type="button" className={classNames(c.report, reportClassName)} onClick={onComplainClick}>
              <ReportIcon className={c.reportIcon} /> Complain
            </button>
          )
        }
      </div>
    </div>
  );
}

ReleaseRateAndShare.propTypes = {
  share: PropTypes.object.isRequired,
  release: PropTypes.object.isRequired,
  reviews: PropTypes.array.isRequired,
  rateClassName: PropTypes.string,
  shareClassName: PropTypes.string,
  reportClassName: PropTypes.string,
  buttonsWrapClassName: PropTypes.string,
  containerClassName: PropTypes.string,
  showComplain: PropTypes.bool,
  onComplainClick: PropTypes.func,
};

ReleaseRateAndShare.defaultProps = {
  rateClassName: null,
  shareClassName: null,
  reportClassName: null,
  buttonsWrapClassName: null,
  containerClassName: null,
  showComplain: false,
  onComplainClick: () => null,
};

export function ReleaseRate({ release, reviews, className }) {
  const hasApprovedReviews = reviews.some((review) => review.reviewState === 'APPROVED');

  return hasApprovedReviews && (
    <div className={classNames(c.topRate, className)}>
      <Rate
        allowHalf
        disabled
        character={<StartIcon />}
        value={roundToHalf(release.overallReviewRate)}
      />
    </div>
  );
}

ReleaseRate.propTypes = {
  release: PropTypes.object.isRequired,
  reviews: PropTypes.array.isRequired,
  className: PropTypes.string,
};

ReleaseRate.defaultProps = {
  className: null,
};
